/**
 * Storage-URL domain (pure). No `SupabaseClient`, no `process.env`, no React.
 *
 * The DB is being moved OFF absolute Supabase URLs and ONTO bucket-relative
 * paths (`listings/foo.jpg`) so a prod dump becomes host-agnostic and
 * `make db-restore` stops rewriting every image URL to the wrong host.
 * See `.tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md` for the
 * phase plan (A: resolver + read funnels, B: writers, D: file copy, C: SQL).
 *
 * Contract decisions (handoff §7, signed off 2026-07-27):
 *  - Q1  stored path carries NO bucket name and NO leading slash
 *        (`listings/foo.jpg`) — exactly what `StoragePath.*` already returns.
 *  - Q3  a legacy ABSOLUTE supabase URL is passed through **forever**. The
 *        resolver never re-hosts it onto the current base: the DB must not
 *        lie, and a silent re-host would mask an incomplete phase-C migration
 *        (the §4 trip-wire depends on an unmigrated row rendering as a
 *        visibly broken image, not as a quietly-working one).
 *
 * Columns that stay ABSOLUTE **by design** (phase C must never touch them —
 * they are historical snapshots/logs, and the host is part of the recorded
 * fact): `stripe_checkout_drafts.checkout_payload`,
 * `moderation_attempts.payload_excerpt`, `reports.reported_avatar_url`,
 * `partial_payment_alerts.metadata`. The passthrough branch below is what
 * keeps them rendering. Full SoT-vs-snapshot inventory: handoff §1.1.
 *
 * `resolveStorageUrl` is TOTAL — every string maps to a string, nothing
 * throws except the one genuinely unrecoverable case (a relative path with no
 * configured base, which means the app is misconfigured). Its behaviour table
 * lives in the handoff §3 L1 and is pinned verbatim by
 * `tests/unit/storage-url.test.ts`.
 *
 * Invariant: **idempotency** — `resolve(resolve(x)) === resolve(x)`. The read
 * funnels (`lib/image-thumbnails.ts`, `hooks/use-heic-image.ts`,
 * `lib/getAvatarUrl.ts`, email normalisation) all resolve independently and
 * compose, so double application must be a no-op.
 *
 * `RawImageRef` (re-exported below) is the compile-time half of the same
 * contract: a value straight out of one of the 7 SoT columns is opaque until
 * it passes through here. See `packages/contract/src/raw-image-ref.ts` for why
 * the declaration lives in the contract package and what it protects
 * (handoff §9 display ≠ persistence, §11 item 1).
 *
 * @see lib/storage/public-url.ts        web env adapter (NEXT_PUBLIC_SUPABASE_URL)
 * @see apps/mobile/lib/storage-url.ts   mobile port (EXPO_PUBLIC_SUPABASE_URL)
 * @see packages/contract/src/raw-image-ref.ts  the brand
 */

import type { RawImageRef } from "@kraahl/contract";

export type { RawImageRef };
export {
  asRawImageRef,
  asRawImageRefs,
  rawImageRefValue,
  rawImageRefValues,
} from "@kraahl/contract";

/**
 * Anything this module accepts where an image reference is expected. Callers
 * that still hold a plain `string` (legacy chains, literals, test fixtures)
 * keep working; callers that hold a `RawImageRef` get in without an escape
 * hatch — which is the whole point: `resolveStorageUrl` is the sanctioned exit
 * from the brand.
 */
export type ImageRefInput = string | RawImageRef;

/** The single storage bucket. Mirrored (not imported) in the mobile port. */
export const STORAGE_BUCKET = "kraahl";

/** Public object endpoint — the canonical form we build. */
export const OBJECT_PUBLIC_MARKER = "/storage/v1/object/public/";

/** Image-transform endpoint — thumb URLs produced by `image-thumbnails.ts`. */
export const RENDER_PUBLIC_MARKER = "/storage/v1/render/image/public/";

/**
 * Any URI scheme (`https:`, `blob:`, `data:`, `file:`, `ph:`, `content:`).
 * A bucket-relative storage path can never match: bucket prefixes are
 * `listings/`, `avatars/`, `messages/`, … and a `:` is not produced by
 * `StoragePath.*`.
 */
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

/**
 * `true` when `raw` is something we must hand to the browser untouched:
 * an absolute URL of any scheme, a protocol-relative `//host/…`, or a
 * site-local asset (`/placeholder.svg`).
 *
 * Leading `/` is deliberately NOT a storage path — storage paths are written
 * without it, so the slash is the discriminator between "site asset" and
 * "bucket key".
 */
export function isPassthroughRef(input: ImageRefInput): boolean {
  const raw = refString(input);
  return SCHEME_RE.test(raw) || raw.startsWith("//") || raw.startsWith("/");
}

/**
 * Internal un-brand. `RawImageRef` IS a string at runtime — the brand is
 * erased — so this cast is sound. It is `private` to this module on purpose:
 * delivery code must go through `resolveStorageUrl` (or the documented
 * `rawImageRefValue` escape hatch), not re-derive the cast.
 */
function refString(input: ImageRefInput): string {
  return input as string;
}

/**
 * `true` when `raw` has the shape of a bucket-relative storage key that the
 * app itself would have written (`listings/foo.jpg`, `messages/room/1.jpg`).
 *
 * This is the ACCEPT predicate for write boundaries that used to demand an
 * absolute `${SUPABASE_URL}/storage/v1/object/public/` prefix (see
 * `app/api/listings/create/route.ts`). Keeping that gate closed while phase B
 * switches the writers to paths would 400 every listing create — but simply
 * dropping it would let an arbitrary attacker-supplied URL into `image_urls`,
 * which is what the gate exists to prevent. So: no scheme, no leading slash, no
 * traversal, no whitespace/backslash, at least one `/`, sane length.
 */
export function isRelativeStoragePath(input: ImageRefInput): boolean {
  const raw = refString(input);
  if (!raw || raw.length > 512) return false;
  if (isPassthroughRef(raw)) return false;
  if (/[\s\\]/.test(raw)) return false;
  if (raw.includes("..")) return false;
  const slash = raw.indexOf("/");
  return slash > 0 && slash < raw.length - 1;
}

/**
 * `true` when `value` is a **persisted** image reference — something that lives
 * in storage and can be written to a DB column — as opposed to a client-only
 * `blob:` / `data:` preview of a `File` that has not been uploaded yet.
 *
 * Canonical replacement for the `isHttpUrl = /^https?:\/\//i` predicate that was
 * copy-pasted into 5 creation-flow components. Those filters decide which
 * entries survive into `image_urls`; left as `^https?://` they would silently
 * DROP every freshly-uploaded image the moment writers persist a
 * bucket-relative path instead of an absolute URL.
 *
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L4
 */
export function isPersistedImageRef(value: ImageRefInput): boolean {
  return /^https?:\/\//i.test(refString(value)) || isRelativeStoragePath(value);
}

/** `base` without a trailing slash; `path` without leading slashes. */
function normalizeBase(base: string): string {
  return base.replace(/\/+$/, "");
}

function normalizePath(path: string): string {
  return path.replace(/^\/+/, "");
}

/**
 * Bucket-relative path → absolute public URL.
 *
 * Deliberately dumb: it does not inspect `path` for an accidental `kraahl/`
 * prefix, because stripping it would break the `extractStoragePath ∘
 * buildPublicUrl === id` round-trip that the fitness test pins.
 */
export function buildPublicUrl(base: string, path: string): string {
  return `${normalizeBase(base)}${OBJECT_PUBLIC_MARKER}${STORAGE_BUCKET}/${normalizePath(path)}`;
}

/**
 * Absolute public URL (object OR render/image endpoint, with or without a
 * query string) → bucket-relative path. `null` when the URL is not a public
 * object of OUR bucket.
 *
 * Not percent-decoded — the returned value is the storage key exactly as it
 * appears in the URL, which is what `storage.remove()` / `getPublicUrl()`
 * round-trip on. (`StoragePath.*` sanitises to `[a-z0-9_]`, so encoding is
 * not expected in practice.)
 */
export function extractStoragePath(input: ImageRefInput): string | null {
  const url = refString(input);
  if (!url) return null;

  const marker = url.includes(OBJECT_PUBLIC_MARKER)
    ? OBJECT_PUBLIC_MARKER
    : url.includes(RENDER_PUBLIC_MARKER)
      ? RENDER_PUBLIC_MARKER
      : null;
  if (!marker) return null;

  const afterMarker = url.slice(url.indexOf(marker) + marker.length);
  const withoutQuery = afterMarker.split(/[?#]/)[0] ?? "";

  const bucketPrefix = `${STORAGE_BUCKET}/`;
  if (!withoutQuery.startsWith(bucketPrefix)) return null;

  const path = withoutQuery.slice(bucketPrefix.length);
  return path.length > 0 ? path : null;
}

/**
 * Render-endpoint URL → its object-endpoint twin, query dropped. `null` when
 * `url` is not a `render/image` URL (an object URL, a `blob:`, a foreign host —
 * nothing to downgrade to).
 *
 * WHY THIS EXISTS: `…/render/image/public/…` is a SERVICE, and the object
 * endpoint underneath it is not. Supabase's image transformation is a Pro-plan
 * add-on that some of our own stacks do not run at all (the self-hosted
 * `kraahl-ci` and staging stacks have `[storage.image_transformation]`
 * commented out in `supabase/config.toml`), and on prod it answers a failed
 * transform with **HTTP 400 and a JSON body** — not an image, not a redirect.
 * Either way the bytes are still sitting at the object URL, unresized. So a
 * transform failure is recoverable, and the recovery is one string operation.
 *
 * This is the pure half. The retry policy — one downgrade, never a loop, reset
 * when `src` changes — lives in `hooks/use-image-transform-fallback.ts`, and the
 * crawl gate has its own copy of the same idea for a different purpose
 * (`tests/e2e/image-crawl-rules.ts::renderToObjectUrl`, which uses it to tell a
 * missing transform layer apart from a genuinely missing file).
 *
 * Deliberately NOT the inverse of `imagePresetUrl`: it returns the ORIGINAL,
 * full-size object, so the fallback trades bytes for a visible picture. That is
 * the right trade for a broken image and the wrong one for a default, which is
 * why nothing calls this on the happy path.
 *
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_CONTINUATION_2026_07_28.md §3.2
 */
export function objectUrlFromRenderUrl(input: ImageRefInput): string | null {
  const url = refString(input);
  if (!url || !url.includes(RENDER_PUBLIC_MARKER)) return null;
  const withoutQuery = url.split(/[?#]/)[0] ?? "";
  return withoutQuery.replace(RENDER_PUBLIC_MARKER, OBJECT_PUBLIC_MARKER);
}

/**
 * TOTAL resolver. Behaviour table (handoff §3 L1):
 *
 * | input                                          | output                                  |
 * |------------------------------------------------|-----------------------------------------|
 * | `listings/foo.jpg`                             | `{base}/storage/v1/object/public/kraahl/listings/foo.jpg` |
 * | `https://<any>.supabase.co/storage/v1/object/public/kraahl/x` | passthrough (Q3: forever) |
 * | `…/render/image/public/…?width=200`            | passthrough, query preserved            |
 * | `blob:` / `data:`                              | passthrough (runtime HEIC conversion)   |
 * | `/placeholder.svg`                             | passthrough (site-local asset)          |
 * | `https://picsum.photos/…`                      | passthrough (dev fixtures)              |
 * | `""` / nullish                                 | returned as-is, never throws            |
 *
 * @throws only when `raw` IS a relative storage path and `base` is empty —
 *         i.e. the app has no Supabase URL configured, in which case no image
 *         could render anyway and a silent broken `<img>` is worse than a loud
 *         error. Passthrough values never touch `base` and never throw.
 */
export function resolveStorageUrl(raw: ImageRefInput, base: string): string;
export function resolveStorageUrl(
  raw: ImageRefInput | null | undefined,
  base: string,
): string | null | undefined;
export function resolveStorageUrl(
  input: ImageRefInput | null | undefined,
  base: string,
): string | null | undefined {
  const raw = input == null ? input : refString(input);
  if (!raw) return raw;
  if (isPassthroughRef(raw)) return raw;
  if (!normalizeBase(base)) {
    throw new Error(
      "[storage/url] cannot resolve a relative storage path without a base URL " +
        "(NEXT_PUBLIC_SUPABASE_URL / EXPO_PUBLIC_SUPABASE_URL is empty)",
    );
  }
  return buildPublicUrl(base, raw);
}
